import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ILabelPreviewHtml } from '../../../core/interfaces';

const PREVIEW_FONTS = `
<style>
@font-face{font-family:'DIN 2014';src:url('/assets/fonts/din/DIN-2014-regular.otf') format('opentype');font-weight:400;}
@font-face{font-family:'DIN 2014';src:url('/assets/fonts/din/DIN-2014-DEMI.otf') format('opentype');font-weight:500;}
@font-face{font-family:'DIN 2014';src:url('/assets/fonts/din/DIN-2014-DEMI.otf') format('opentype');font-weight:600;}
@font-face{font-family:'DIN 2014';src:url('/assets/fonts/din/DIN-2014-DEMI.otf') format('opentype');font-weight:700;}
@font-face{font-family:'Monarcha';src:url('/assets/fonts/monarcha/Monarcha-Book.ttf') format('truetype');font-weight:300;}
@font-face{font-family:'Monarcha';src:url('/assets/fonts/monarcha/Monarcha-Regular.ttf') format('truetype');font-weight:400;}
@font-face{font-family:'Monarcha';src:url('/assets/fonts/monarcha/Monarcha-SemiBold.ttf') format('truetype');font-weight:500;}
@font-face{font-family:'Monarcha';src:url('/assets/fonts/monarcha/Monarcha-SemiBold.ttf') format('truetype');font-weight:600;}
@font-face{font-family:'Monarcha';src:url('/assets/fonts/monarcha/Monarcha-Bold.ttf') format('truetype');font-weight:700;}
@font-face{font-family:'MonarchaW01-Regular';src:url('/assets/fonts/monarcha/Monarcha-Regular.ttf') format('truetype');font-weight:400;}
@font-face{font-family:'MonarchaW01-SemiBold';src:url('/assets/fonts/monarcha/Monarcha-SemiBold.ttf') format('truetype');font-weight:600;}
html,body{margin:0;padding:0;background:#fff;overflow:hidden;}
</style>`;

@Component({
  selector: 'app-label-preview-host',
  templateUrl: './label-preview-host.component.html',
  styleUrls: ['./label-preview-host.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LabelPreviewHostComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() preview: ILabelPreviewHtml;
  @Input() loading = false;
  @ViewChild('pane') pane: ElementRef<HTMLElement>;
  @ViewChild('mainFrame') mainFrame: ElementRef<HTMLIFrameElement>;
  @ViewChild('companionFrame') companionFrame: ElementRef<HTMLIFrameElement>;

  public scale = 1;

  private resizeObserver: ResizeObserver;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.observePane();
    this.writeFrames();
    this.scheduleScale();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['preview']) {
      setTimeout(() => {
        this.writeFrames();
        this.observePane();
        this.scheduleScale();
      });
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.updateScale();
  }

  public onFrameLoad(): void {
    this.scheduleScale();
  }

  get hasCompanion(): boolean {
    return !!this.preview?.companionHtml;
  }

  get frameWidthMm(): number {
    if (!this.preview) {
      return 1;
    }
    return Math.max(this.preview.widthMm || 1, this.hasCompanion ? (this.preview.companionWidthMm || 58) : 0);
  }

  get frameHeightMm(): number {
    if (!this.preview) {
      return 1;
    }
    return this.preview.heightMm + (this.hasCompanion ? (this.preview.companionHeightMm || 58) + 8 : 0);
  }

  get scaledWidthPx(): number {
    return this.frameWidthMm * (96 / 25.4) * this.scale;
  }

  get scaledHeightPx(): number {
    return this.frameHeightMm * (96 / 25.4) * this.scale;
  }

  private writeFrames(): void {
    if (!this.preview?.html) {
      return;
    }
    const origin = window.location.origin;
    writeIframe(this.mainFrame?.nativeElement, withPreviewAssets(this.preview.html, origin));
    if (this.hasCompanion) {
      writeIframe(this.companionFrame?.nativeElement, withPreviewAssets(this.preview.companionHtml, origin));
    }
  }

  private observePane(): void {
    const pane = this.pane?.nativeElement;
    if (!pane || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.updateScale());
    this.resizeObserver.observe(pane);
  }

  private scheduleScale(): void {
    this.updateScale();
    requestAnimationFrame(() => this.updateScale());
    setTimeout(() => this.updateScale(), 50);
    setTimeout(() => this.updateScale(), 250);
  }

  private updateScale(): void {
    const pane = this.pane?.nativeElement;
    if (!pane || !this.preview) {
      return;
    }
    const pad = 48;
    const availableW = Math.max(pane.clientWidth - pad, 1);
    const availableH = Math.max(pane.clientHeight - pad, 1);
    if (availableW < 80 || availableH < 80) {
      return;
    }
    const mm = 96 / 25.4;
    const naturalW = Math.max(this.frameWidthMm * mm, 1);
    const naturalH = Math.max(this.frameHeightMm * mm, 1);
    const next = Math.min(availableW / naturalW, availableH / naturalH);
    if (!isFinite(next) || next <= 0) {
      return;
    }
    this.scale = next;
    this.cdr.markForCheck();
  }
}

function writeIframe(frame: HTMLIFrameElement | undefined, html: string): void {
  const doc = frame?.contentDocument;
  if (!doc) {
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();
}

function withPreviewAssets(html: string, origin: string): string {
  const chrome = `<base href="${origin}/">${PREVIEW_FONTS}`;
  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, `${chrome}</head>`);
  }
  return chrome + html;
}
